import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { AgentProfileCardTrigger } from '@atlaskit/profilecard';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';
import { token } from '@atlaskit/tokens';
import { type Agent } from '@atlassian/conversation-assistant-service';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { QueryIntent } from '../../constants';
import { KnowledgeCarousel } from '../../knowledge-card-components/knowledge-carousel';
import { useMeaningfulHover } from '../../v2/common/hooks/use-meaningful-hover';
import { AIDisclaimerAndFeedback } from '../../v2/common/ui/ai-disclaimer-and-feedback';
import {
	onAgentKnowledgeCardCarouselItemClick,
	onAgentKnowledgeCardCarouselShown,
	onIndividualAgentKnowledgeCardShown,
	onKnowledgeCardShown,
} from '../analytics';
import { KnowledgeCardLoading } from '../knowledge-card-loading';
import { SeparatorLineRefresh } from '../separator-line';
import { KnowledgeCardsIntentType } from '../types';
import { createProfileClient, getAgentProfileUrl } from '../util';

import { AgentCardBody } from './agent-card-body';
import { AgentCardHeader } from './agent-card-header';
import { useAgentCardData } from './use-agent-card-data';

const CardContainerStyles = cssMap({
	refresh: {
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		gap: token('space.200'),
	},
	disclaimer: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
});

const AgentCardWrapperStyles = cssMap({
	wrapper: {
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		transition: 'box-shadow 0.25s ease-in-out',
		overflow: 'hidden',
		'&:hover': {
			cursor: 'pointer',
			textDecoration: 'none',
			transitionProperty: 'background-color',
			transitionDuration: '400ms',
			transitionTimingFunction: 'ease-in-out',
			transitionDelay: '0ms',
		},
		flexBasis: '180px',
		flexGrow: 0,
		flexShrink: 0,
		width: '180px',
		minWidth: '160px',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		position: 'relative',
		whiteSpace: 'normal',
	},
});

type IndividualAgentKnowledgeCardProps = {
	agent: Agent | undefined;
};

const AGENTS_SCROLL_OFFSET = 392;

const messages = defineMessages({
	multiple_agents_matches_header: {
		id: 'search_ai.knowledge_cards.multiple_agents_matches_header',
		defaultMessage: 'Agent matches',
		description: 'Header for the multiple agents carousel',
	},
});

const IndividualAgentKnowledgeCard = ({ agent }: IndividualAgentKnowledgeCardProps) => {
	const { teamsData, activityData, loading } = useAgentCardData(agent);
	const { commonAttributes } = useAIAnswerContext();
	const { orgId } = commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();

	// Fire analytics event when individual agent knowledge card is shown
	React.useEffect(() => {
		if (agent) {
			fireAnalyticsEvent(
				onIndividualAgentKnowledgeCardShown({
					...commonAttributes,
					agentId: agent.id,
				}),
			);
		}
	}, [agent, commonAttributes, fireAnalyticsEvent]);

	if (!agent) {
		return null;
	}

	if (loading) {
		return <KnowledgeCardLoading queryIntent={QueryIntent.AGENT} />;
	}

	const showKnowledgeCardBody =
		(teamsData?.teamSearch?.nodes && teamsData.teamSearch.nodes.length > 0) ||
		(activityData && Array.isArray(activityData) && activityData.length > 0);

	return (
		<>
			<Stack xcss={CardContainerStyles.refresh}>
				<AgentCardHeader agent={agent} />
				{showKnowledgeCardBody && <SeparatorLineRefresh />}
				<Box>
					{showKnowledgeCardBody && (
						<AgentCardBody
							currentUserId={agent.identity_account_id || ''}
							teamsData={teamsData}
							activityData={activityData}
							profileUrl={getAgentProfileUrl(agent, orgId)}
						/>
					)}
					<Box xcss={CardContainerStyles.disclaimer}>
						<AIDisclaimerAndFeedback showFeedbackButtons />
					</Box>
				</Box>
			</Stack>
		</>
	);
};

export const AgentKnowledgeCard = ({
	onFMPReached,
}: {
	onFMPReached?: () => void;
}): React.JSX.Element | null => {
	const { commonAttributes, loading, userDetails } = useAIAnswerContext();
	const { cloudId } = commonAttributes;
	const { formatMessage } = useIntl();
	const [fireAnalyticsEvent] = useAnalytics();
	const [selectedAgent, setSelectedAgent] = useState<Agent | undefined>();
	const { answerString } = commonAttributes;

	// Parses the answerString to extract agent results, returning an empty array if parsing fails or if the result is not an array.
	const agentResults: Agent[] = useMemo(() => {
		if (!answerString) {
			return [];
		}
		try {
			const parsed = JSON.parse(answerString);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}, [answerString]);

	// Fire analytics event when carousel is shown (only when multiple agents exist)
	React.useEffect(() => {
		if (agentResults && agentResults.length > 1) {
			fireAnalyticsEvent(
				onAgentKnowledgeCardCarouselShown({
					...commonAttributes,
					agentCount: agentResults.length,
				}),
			);
		}
	}, [agentResults, commonAttributes, fireAnalyticsEvent]);

	const profileClient = useMemo(() => createProfileClient(cloudId), [cloudId]);

	const getHandleSelectAgent = useCallback(
		(index: number) => () => {
			const selectedAgentData = agentResults[index];
			setSelectedAgent(selectedAgentData);

			// Fire analytics event for carousel item click
			fireAnalyticsEvent(
				onAgentKnowledgeCardCarouselItemClick({
					...commonAttributes,
					agentId: selectedAgentData.id,
					position: index,
				}),
			);
		},
		[agentResults, commonAttributes, fireAnalyticsEvent],
	);

	useEffect(() => {
		if (agentResults.length === 1) {
			setSelectedAgent(agentResults[0]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [agentResults]);

	useEffect(() => {
		onFMPReached?.();
		fireAnalyticsEvent(
			onKnowledgeCardShown({
				...commonAttributes,
				cardType: KnowledgeCardsIntentType.AGENT,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { handleHoverStart, handleHoverLeave } = useMeaningfulHover({
		elementType: 'agent',
		hoverCardSelector: '[data-testid="agent-knowledge-card-trigger"]',
	});

	if (loading) {
		return <KnowledgeCardLoading />;
	}

	if (agentResults.length === 0) {
		return null;
	}

	if (selectedAgent !== undefined || agentResults.length === 1) {
		return <IndividualAgentKnowledgeCard agent={selectedAgent} />;
	}

	return (
		<Stack space="space.150">
			<KnowledgeCarousel
				heading={formatMessage(messages.multiple_agents_matches_header)}
				scrollOffset={AGENTS_SCROLL_OFFSET}
			>
				{agentResults.map((agent, index) => (
					// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
					<Box
						onClick={getHandleSelectAgent(index)}
						testId="agent-knowledge-card-wrapper"
						key={agent.id}
						xcss={AgentCardWrapperStyles.wrapper}
						onMouseEnter={handleHoverStart}
						onMouseLeave={handleHoverLeave}
					>
						<AgentProfileCardTrigger
							trigger="hover"
							agentId={agent.id}
							resourceClient={profileClient}
							cloudId={cloudId}
							viewingUserId={userDetails.id}
							testId="agent-knowledge-card-trigger"
						>
							<Stack space="space.150" alignInline="center">
								<AgentAvatar
									agentId={agent.id}
									agentNamedId={agent.id}
									agentIdentityAccountId={agent.identity_account_id}
									size="xxlarge"
								/>
								<Stack space="space.100" alignInline="center">
									<Text maxLines={1} size="medium" weight="medium" align="center">
										{agent.name}
									</Text>
									{agent.description && (
										<Text maxLines={1} size="medium" color="color.text.subtlest" align="center">
											{agent.description}
										</Text>
									)}
								</Stack>
							</Stack>
						</AgentProfileCardTrigger>
					</Box>
				))}
			</KnowledgeCarousel>
			<AIDisclaimerAndFeedback showFeedbackButtons />
		</Stack>
	);
};
